/**
 * FilterButton - the "Filters" call-to-action that opens a filter pop-up.
 *
 * Atomic Design role: Atom. Below the desktop breakpoint a page's filter row
 * collapses into this one button (see the ResponsiveFilters organism), so the
 * table underneath gets the full width. It carries a count of the filters
 * currently applied, so a narrowed list never looks like "all the data" —
 * that count is the only clue left on screen once the controls are tucked away.
 *
 * Outlined, not filled: it sits in toolbars beside search boxes and the page's
 * real primary action ("Add User", "New Order"), and mustn't outshout them.
 *
 * @example
 * <FilterButton activeCount={2} onClick={() => setOpen(true)} />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface FilterButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
	/** How many filters are applied; 0 hides the count. */
	activeCount?: number;
	/** Visible text — defaults to "Filters". */
	label?: string;
	/** Overrides the atom's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const FilterButton: React.FC<FilterButtonProps> = ({ activeCount = 0, label = 'Filters', dataRef = 'atom-filter-button', type = 'button', className = '', ...rest }) => {
	const active = activeCount > 0;
	return (
		<button
			type={type}
			/* Spell the count out for screen readers; the badge alone is just a number. */
			aria-label={active ? `${label}, ${activeCount} applied` : label}
			className={[
				'filter-button',
				active ? 'filter-button--active' : '',
				'inline-flex items-center justify-center gap-8px shrink-0 px-16px min-h-44px rounded-6px border bg-white font-sans text-14px font-semibold transition-colors',
				active ? 'border-brand-primary text-brand-primary' : 'border-brand-border text-brand-text hover:border-brand-primary hover:text-brand-primary',
				className,
			].filter(Boolean).join(' ')}
			data-ref={dataRef}
			{...rest}
		>
			{/* Sliders icon — the same glyph the Work Orders filter button has always used. */}
			<svg className="filter-button__icon w-16px h-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
				<path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
			</svg>
			<span className="filter-button__label">{label}</span>
			{active && (
				<span className="filter-button__count inline-flex items-center justify-center min-w-20px h-20px px-6px rounded-full bg-brand-primary text-white text-12px font-bold" aria-hidden="true">
					{activeCount}
				</span>
			)}
		</button>
	);
};

export default FilterButton;
